/**
 * Deck primitives for Manille (Belgian variant).
 *
 * 32 cards. Ranking, strongest first:
 *   10 (manille) > A (manillon) > K > Q > J > 9 > 8 > 7
 * Card points:
 *   10 = 5, A = 4, K = 3, Q = 2, J = 1, 9/8/7 = 0   →  60 points per deal.
 *
 * A card is a plain string like `10h` / `As` / `7d` so it survives JSON, `Set`,
 * `Map` keys and DOM `data-` attributes without any identity games.
 */

export const SUITS = ['c', 'd', 'h', 's'] as const;
export type Suit = (typeof SUITS)[number];

/** Descending strength. Index 0 is the strongest card of a suit. */
export const RANKS = ['10', 'A', 'K', 'Q', 'J', '9', '8', '7'] as const;
export type Rank = (typeof RANKS)[number];

/** All 32 concrete card ids, as a literal union. */
export type Card = `${Rank}${Suit}`;

const RANK_POINTS: Record<Rank, number> = {
  '10': 5,
  A: 4,
  K: 3,
  Q: 2,
  J: 1,
  '9': 0,
  '8': 0,
  '7': 0,
};

/** Higher number = stronger card. `10` is 7, `7` is 0. */
const RANK_STRENGTH: Record<Rank, number> = Object.fromEntries(
  RANKS.map((rank, index) => [rank, RANKS.length - 1 - index]),
) as Record<Rank, number>;

export const SUIT_NAMES: Record<Suit, string> = {
  c: 'klaveren',
  d: 'ruiten',
  h: 'harten',
  s: 'schoppen',
};

export const SUIT_SYMBOLS: Record<Suit, string> = {
  c: '♣',
  d: '♦',
  h: '♥',
  s: '♠',
};

export const RANK_LABELS: Record<Rank, string> = {
  '10': '10',
  A: 'A',
  K: 'K',
  Q: 'Q',
  J: 'J',
  '9': '9',
  '8': '8',
  '7': '7',
};

export const DECK: readonly Card[] = SUITS.flatMap((suit) =>
  RANKS.map((rank) => `${rank}${suit}` as Card),
);

export const TOTAL_DEAL_POINTS = 60;

/** Suit is the last character; rank is everything before it (handles `10`). */
export function cardSuit(card: Card): Suit {
  return card.slice(-1) as Suit;
}

export function cardRank(card: Card): Rank {
  return card.slice(0, -1) as Rank;
}

export function cardPoints(card: Card): number {
  return RANK_POINTS[cardRank(card)];
}

export function cardStrength(card: Card): number {
  return RANK_STRENGTH[cardRank(card)];
}

export function isTrump(card: Card, trump: Suit): boolean {
  return cardSuit(card) === trump;
}

export function pointsOf(cards: readonly Card[]): number {
  let total = 0;
  for (const card of cards) total += cardPoints(card);
  return total;
}

export function isCard(value: unknown): value is Card {
  return typeof value === 'string' && (DECK as readonly string[]).includes(value);
}

/** `10h` → `10♥`. Used in illegal-move explanations, shared by both ends. */
export function cardLabel(card: Card): string {
  return `${RANK_LABELS[cardRank(card)]}${SUIT_SYMBOLS[cardSuit(card)]}`;
}

export function suitLabel(suit: Suit): string {
  return SUIT_SYMBOLS[suit];
}

const SUIT_IS_RED: Record<Suit, boolean> = { c: false, d: true, h: true, s: false };

function permutations<T>(items: readonly T[]): T[][] {
  if (items.length <= 1) return [[...items]];
  const out: T[][] = [];
  items.forEach((item, index) => {
    const rest = [...items.slice(0, index), ...items.slice(index + 1)];
    for (const tail of permutations(rest)) out.push([item, ...tail]);
  });
  return out;
}

/**
 * The order the suit groups appear in, left to right. Neighbouring groups
 * alternate colour wherever the hand allows it — two red groups side by side
 * blur into one pile at a glance, which is how a card gets played by mistake.
 * Trump stays on the right, where it is always looked for.
 *
 * At most four groups, so trying every arrangement and keeping the one with
 * the fewest same-colour neighbours is both exact and free; `SUITS` order
 * decides ties, so a hand always fans the same way.
 */
function groupOrder(present: readonly Suit[], trump: Suit | null): Suit[] {
  const pinned = trump !== null && present.includes(trump) ? trump : null;
  const movable = present.filter((suit) => suit !== pinned);
  let best: Suit[] = [];
  let fewest = Infinity;
  for (const arrangement of permutations(movable)) {
    const order = pinned === null ? arrangement : [...arrangement, pinned];
    let clashes = 0;
    for (let i = 1; i < order.length; i += 1) {
      if (SUIT_IS_RED[order[i] as Suit] === SUIT_IS_RED[order[i - 1] as Suit]) clashes += 1;
    }
    if (clashes < fewest) {
      fewest = clashes;
      best = order;
    }
  }
  return best;
}

/**
 * Sort for a player's fan: group by suit, strongest first inside a suit, with
 * the groups laid out by `groupOrder`.
 */
export function sortHand(hand: readonly Card[], trump: Suit | null): Card[] {
  const present = SUITS.filter((suit) => hand.some((card) => cardSuit(card) === suit));
  const order = groupOrder(present, trump);
  const rank = new Map(order.map((suit, index) => [suit, index]));
  return [...hand].sort((a, b) => {
    const bySuit = (rank.get(cardSuit(a)) ?? 0) - (rank.get(cardSuit(b)) ?? 0);
    if (bySuit !== 0) return bySuit;
    return cardStrength(b) - cardStrength(a);
  });
}
